import {
  Internship,
  Scholarship,
  CareerPath,
  ContentNugget,
  CareerPathScoreSection,
  CareerPathResponse,
  BackrsIcons,
  College,
} from '@backrs_ui/common-ui';

import { ToolId } from '@backrs_ui/grow-ui/src/pages/Home/hooks/stickerCardMapping';

export const SCHOOL_LEVEL = {
  HIGH_SCHOOL: 'high_school',
  COLLEGE: 'college',
  TRADE: 'trade',
  NO_CURRENT_ENROLLMENT: 'no_current_enrollment',
} as const;

export type SchoolLevel = (typeof SCHOOL_LEVEL)[keyof typeof SCHOOL_LEVEL];

export type CareerQuestionFacts = Readonly<{
  type: 'TEXT' | 'LINK';
  value: string;
  sequence: number;
  url?: string;
}>;

type CareerQuestionLogic = Readonly<{
  /** which question are we targeting */
  logic_question_id: number;
  /** which question is this logic associated with */
  career_question_id: number;
  career_question_option_id: number;
  action: 'HIDE' | 'SHOW';
}>;

export type CareerQuestion = Readonly<{
  id: number;
  calculator_key: string;
  title: string;
  question: string;
  facts?: CareerQuestionFacts[];
  aspirational_title?: string;
  sequence: number;
  is_multi_select?: boolean;
  is_custom_response?: boolean;
  school_level: SchoolLevel | 'both';
  conditional_logic?: CareerQuestionLogic[];
  options: CareerQuestionOption[];
}>;

type CareerQuestionOption = Readonly<{
  id: number;
  calculator_key: string;
  career_question_id: number;
  answer: string;
  sequence: number;
  point_value?: number;
  aspirational_title?: string;
}>;

export type CareerUserResponseSet = Readonly<{
  id: string; // uuid
  user_responses: CareerUserResponseAnswer[];
  school_level?: SchoolLevel;
  created_at?: string;
}>;

export type CareerUserResponseAnswer = Readonly<{
  id: string; // uuid
  career_user_response_set_id: string; // uuid
  career_question_id: number;
  career_question_option_ids: number[];
  career_question_option_custom_text?: string;
}>;

export type CareerPathWithScore = Readonly<{
  careerPath: CareerPath;
  sum: number;
  scoreSection: CareerPathScoreSection;
}>;

export type CareerRole = Readonly<{
  id: number;
  name: string;
}>;

export type CareerPathRole = Readonly<{
  id: number;
  career_role_id: number;
  career_path_id: number;
  tier: 'trade' | 'middle' | 'upper' | 'top';
}>;

export type CareerPathEnjoyment = Readonly<{
  id: number;
  career_path_id: number;
  working_with_hands_value: number;
  creating_value: number;
  promoting_or_selling_value: number;
  serving_or_caring_value: number;
  coaching_teaching_advising_value: number;
  analyzing_and_solving_problems_value: number;
  collaborating_value: number;
  making_decisions_value: number;
  following_a_playbook_value: number;
  leading_people_value: number;
}>;

export type CareerPathMeaning = Readonly<{
  id: number;
  career_path_id: number;
  relationships_value: number;
  legacy_value: number;
  personal_growth_value: number;
  social_impact_value: number;
  innovation_value: number;
  connection_to_nature_value: number;
  creativity_value: number;
  benefiting_others_value: number;
  honoring_spiritual_beliefs_value: number;
}>;

export const LUBAV_SUPERSESSIONS = {
  SUPERCOACH: 'supercoach',
  SUPERUSER: 'superuser',
};

export type LubavSupersession = (typeof LUBAV_SUPERSESSIONS)[keyof typeof LUBAV_SUPERSESSIONS];

export const SOLUTION_SESSION_IDS = {
  COLLEGE_SHORTLIST: 'college_shortlist',
  IMPROVE_YOUR_TEST_SCORES: 'improve_your_test_scores',
  WHAT_ARE_YOUR_CHANCES: 'what_are_your_chances',
  LOWER_COST_OF_COLLEGE: 'lower_cost_of_college',
  JOBS_YOU_MIGHT_LOVE: 'jobs_you_might_love',
  COMMON_THREADS: 'common_threads',
  LETTERS_OF_REC: 'letters_of_rec',
  DEFAULT: 'default',
} as const;

export type SolutionSessionId = (typeof SOLUTION_SESSION_IDS)[keyof typeof SOLUTION_SESSION_IDS];

export const solutionSessionIconMap: Record<SolutionSessionId, BackrsIcons> = {
  college_shortlist: 'CollegeShortlistIcon',
  what_are_your_chances: 'WhatAreYourChancesIcon',
  improve_your_test_scores: 'ImproveYourTestScoresIcon',
  lower_cost_of_college: 'LowerCostOfCollegeIcon',
  jobs_you_might_love: 'JobsYouMightLoveIcon',
  common_threads: 'CommonThreadsIcon',
  letters_of_rec: 'LettersOfRecommendationIcon',
  default: 'IconSolutionSession',
};

export const OFFICE_HOURS_SESSION_TYPES = {
  BOOST_YOUR_PROFILE: 'boost_your_profile',
  BOOST_ODDS_OF_AN_OFFER: 'boost_odds_of_an_offer',
  RESUME_OPTIMIZER: 'resume_optimizer',
  ESSAY_IDEAS: 'essay_ideas',
  WHAT_ARE_YOUR_CHANCES: 'what_are_your_chances',
} as const;

export type OfficeHoursSessionType =
  (typeof OFFICE_HOURS_SESSION_TYPES)[keyof typeof OFFICE_HOURS_SESSION_TYPES];

export type LubavSessionType = LubavSupersession | OfficeHoursSessionType | SolutionSessionId;

export const OFFICE_HOURS_SESSION_TITLES = {
  [OFFICE_HOURS_SESSION_TYPES.BOOST_YOUR_PROFILE]: 'Boost your profile',
  [OFFICE_HOURS_SESSION_TYPES.BOOST_ODDS_OF_AN_OFFER]: 'Boost odds of an offer',
  [OFFICE_HOURS_SESSION_TYPES.RESUME_OPTIMIZER]: 'Resume optimizer',
  [OFFICE_HOURS_SESSION_TYPES.ESSAY_IDEAS]: 'Essay ideas',
  [OFFICE_HOURS_SESSION_TYPES.WHAT_ARE_YOUR_CHANCES]: 'What are your chances?',
  [LUBAV_SUPERSESSIONS.SUPERCOACH]: 'Super Coach',
  [LUBAV_SUPERSESSIONS.SUPERUSER]: 'Super User',
} as const;

export type LubavContact = Readonly<{
  id: string;
  relevance?: string;
  metadata: {
    name: string;
    role: 'professional' | 'student' | 'foundations';
    school: string | null;
    job_title: string;
    location: string;
    employer?: string;
    link: string;
    profile_type?: 'external' | 'backr';
    profile_image?: string;
    relevance_score?: number;
  };
}>;

export type LubavCompetency = Readonly<{
  id: string;
  relevance: string;
  metadata: {
    description: string;
    title: string;
    relevance_score?: number;
  };
}>;

export type LubavInternship = Readonly<{
  relevance: string;
  metadata: Internship & { relevance_score?: number };
}>;

export type LubavPost = Readonly<{
  id: string;
  relevance: string;
  quote: string;
  quote_author: string;
  metadata: {
    author: string;
    created_at: string;
    author_profile_picture: string;
    relevance_score?: number;
  };
}>;

export type LubavResource = Readonly<{
  id: string;
  relevance: string;
  metadata?: ContentNugget & { relevance_score?: number };
}>;

export type LubavCareerPath = Readonly<{
  id: string;
  relevance: string;
  metadata: CareerPathResponse & { relevance_score?: number };
}>;

export type LubavTool = Readonly<{
  id: ToolId;
  relevance: string;
  metadata: {
    relevance_score?: number;
  };
}>;

export type LubavCollege = Readonly<{
  id: string;
  relevance: string;
  metadata: College & { relevance_score?: number };
}>;

export const RESOURCE_KEY = {
  RESOURCES: 'resources',
  COMPETENCIES: 'competencies',
  BACKRS: 'backrs',
  STUDENTS: 'students',
  SCHOLARSHIPS: 'scholarships',
  INTERNSHIPS: 'internships',
  TOOLS: 'tools',
  CAREER_PATHS: 'career_paths',
  PROVIDERS: 'providers',
  COLLEGES: 'colleges',
  LEARNING: 'learning',
  STORIES: 'stories',
} as const;

export type ResourceKey = (typeof RESOURCE_KEY)[keyof typeof RESOURCE_KEY];

export type ResourcesContent = Readonly<{
  [RESOURCE_KEY.BACKRS]?: { content: LubavContact[] };
  [RESOURCE_KEY.STUDENTS]?: { content: LubavContact[] };
  [RESOURCE_KEY.RESOURCES]?: { content: LubavResource[] };
  [RESOURCE_KEY.COMPETENCIES]?: { content: LubavCompetency[] };
  [RESOURCE_KEY.SCHOLARSHIPS]?: { content: LubavScholarship[] };
  [RESOURCE_KEY.INTERNSHIPS]?: { content: LubavInternship[] };
  [RESOURCE_KEY.TOOLS]?: { content: LubavTool[] };
  [RESOURCE_KEY.CAREER_PATHS]?: { content: LubavCareerPath[] };
  [RESOURCE_KEY.PROVIDERS]?: { content: LubavContact[] };
  [RESOURCE_KEY.COLLEGES]?: { content: LubavCollege[] };
  [RESOURCE_KEY.STORIES]?: { content: LubavResource[] };
  [RESOURCE_KEY.LEARNING]?: { content: LubavResource[] };
}>;

type StudyPlanWeek = Readonly<{
  goals: string[];
  monday?: string[];
  tuesday?: string[];
  wednesday?: string[];
  thursday?: string[];
  friday?: string[];
  saturday?: string[];
  sunday?: string[];
}>;

type StudyPlan = Record<string, StudyPlanWeek>;

type CollectedDataBox = Readonly<{
  name: string[];
  value: string[];
}>;

export type CollectedData = Readonly<{
  box_1: CollectedDataBox;
  box_2: CollectedDataBox;
  hidden: CollectedDataBox;
}>;

export type School = Readonly<{
  ipeds_id: string;
  relevance: string;
  highlights: string;
  considerations: string;
  metadata: {
    school_name?: string;
    description?: string;
    url?: string;
    logo_media_url?: string;
    banner_media_url?: string;
  };
}>;

export type LubavScholarship = Readonly<{
  id: number;
  relevance: string;
  metadata: Scholarship & { relevance_score: number };
}>;

export type Job = Readonly<{
  metadata: Internship;
  benefits: string;
  goals_fit: string;
  goals_discrepency: string;
  qualified: string;
  not_qualified: string;
}>;

export type MainResponse = Readonly<{
  safe: School[];
  target: School[];
  stretch: School[];
  scholarships?: LubavScholarship[];
  priorities?: { priorities: string[] };
  plan?: StudyPlan;
  opportunities?: Job[];
  letter?: string;
  action_plan?: string;

  // SUCCESS DECODED
  id?: string;
  commonalities?: string;
  relevance?: string;
  insights?: string[];
  questions?: string[];
  metadata?: LubavContact['metadata'];
}>;

type FinalMessage = Readonly<{
  text: string;
  question: string | null;
  finished: boolean;
  query_id: number;
  response_id: number;
  ask_to_regenerate: boolean;
}>;

export type MainResponseData = Readonly<{
  main_response?: MainResponse;
  resources?: ResourcesContent;
  final_message?: FinalMessage;
}>;

export type BoxData = Readonly<{
  mainResponse: MainResponseData;
  collectedData: CollectedData;
}>;

export type SearchedUser = Readonly<{
  id: string;
  name: string;
  relevance: number;
  profile_image?: string;
}>;

export const SENDER_TYPE = {
  USER: 'user',
  AI: 'ai',
} as const;

export type SenderType = (typeof SENDER_TYPE)[keyof typeof SENDER_TYPE];

export const MESSAGE_MODE = {
  QA: 'qa',
  CHAT: 'chat',
} as const;

export type MessageMode = (typeof MESSAGE_MODE)[keyof typeof MESSAGE_MODE];

export type Contact = Readonly<{
  id: string;
  relevance: string;
  metadata: {
    name: string;
    role: 'professional' | 'student' | 'foundations';
    school: string | null;
    job_title: string;
    location: string;
    employer?: string;
    link: string;
    profile_type?: 'external' | 'backr';
    profile_image?: string;
  };
}>;

export type BaseMessage = Readonly<{
  id?: string | null;
  text?: string;
  sender: SenderType;
  componentType?:
    | { type: 'TYPING_INDICATOR' }
    | { type: 'SELECTED_USER'; user: SearchedUser }
    | { type: 'FILE_UPLOAD'; documentType?: string }
    | { type: 'CONFIRM_DOCUMENT'; documentType?: string };
}>;

export type OfficeHoursMessage = BaseMessage &
  Readonly<{
    nuggets?: {
      users: Contact[]; // other nugget types may be added
    };
  }>;

export type SolutionSessionMessage = BaseMessage &
  Readonly<{
    mode?: MessageMode;
    question?: string;
    type?: string;
    options?: string[];
    include_freeform?: boolean;
    required?: boolean;
    user_selection_options?: Contact[];
    placeholder?: string;
    ask_to_regenerate?: boolean;
  }>;

export const SOURCES_OF_TRUTH = {
  ALL: 'all',
  BACKRS_VALIDATED: 'backrs-validated',
  MY_NETWORKS: 'my-networks',
  MY_INSTITUTION: 'my-institution',
  PEOPLE_LIKE_ME: 'people-like-me',
} as const;

export type SourcesOfTruth = (typeof SOURCES_OF_TRUTH)[keyof typeof SOURCES_OF_TRUTH];

export const COACHING_STYLES = {
  CONVERSATIONAL: 'conversational',
  DIRECT: 'direct',
  STRATEGIC: 'strategic',
} as const;

export type CoachingStyle = (typeof COACHING_STYLES)[keyof typeof COACHING_STYLES];

export const APP_LANGUAGES = {
  ENGLISH: 'english',
  SPANISH: 'spanish',
  FRENCH: 'french',
} as const;

export type AppLanguage = (typeof APP_LANGUAGES)[keyof typeof APP_LANGUAGES];

export const OPENAI_MODELS = {
  SMARTEST: 'smartest',
  FASTEST: 'fastest',
  THINKING: 'thinking',
} as const;

export type OpenAIModel = (typeof OPENAI_MODELS)[keyof typeof OPENAI_MODELS];

export type CostTypes = 'total_cost' | 'prompt_tokens' | 'completion_tokens';

export const DOCUMENT_TYPES = {
  RESUME: 'resume',
  TRANSCRIPT: 'transcript',
  OTHER: 'other',
} as const;

export type DocumentType = (typeof DOCUMENT_TYPES)[keyof typeof DOCUMENT_TYPES];

export type PromptItem = Readonly<{
  display: string;
  input?: string;
  hidden?: boolean;
  requireDocument?: DocumentType;
}>;
